import React, { MouseEventHandler, useEffect, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Connection, Destination, Organization } from "../common/ObjectTypes";
import DestinationDetailsModalTable from "./DestinationDetailsModalTable";
import DestinationDetailsModalConfig from "./DestinationDetailsModalConfig";
import { connectionDetailsClasses } from "../common/tailwindClassnames";

type DestinationDetailsProps = {
    currentMemberIsAdmin: boolean,
    destination: Destination,
    destinationTitle: string,
    destSubPage: string | undefined,
    onSelectSubPage: Function,
    connections: Connection[],
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onToggleModal: MouseEventHandler,
    requestSort: Function,
    loadingConnections: boolean,
    onChangeSelectedDestination: Function,
    onChangeSelectedDestinationConfig: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number,
    onChangeSelectedDestinationOwner: Function,
    onSave: (e: React.FormEvent<Element>, action?: "save" | "saveAndClose") => void,
    errors: any,
    rsjfErrors: any,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    scrollableContentRef?: RefObject<HTMLDivElement>
}

const DestinationDetailsModal = ({
    currentMemberIsAdmin,
    destination,
    destinationTitle,
    destSubPage,
    onSelectSubPage,
    connections,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onToggleModal,
    requestSort,
    loadingConnections,
    onChangeSelectedDestination,
    onChangeSelectedDestinationConfig,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus,
    onChangeSelectedDestinationOwner,
    onSave,
    errors,
    rsjfErrors,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    scrollableContentRef
}: DestinationDetailsProps) => {
    const { t } = useTranslation();

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    // Only allow 'details' or 'configuration' as valid subpages
    const allowedSubPages = ['details', 'configuration'];
    const subPage = allowedSubPages.includes(destSubPage || '') ? destSubPage : 'details';

    if (!pageLoaded) return (
        <div className="z-20 flex flex-col w-full h-screen bg-white dark:bg-neutral-gray-900 transition-colors duration-300" role="status" aria-busy="true">
            <span className="sr-only">Loading...</span>
            {/* Header skeleton */}
            <div className={connectionDetailsClasses.header}>
                <div className="flex flex-col pb-2 w-full">
                    <div className="h-8 w-56 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse mb-1" />
                    <div className="flex flex-row gap-4">
                        <div className="h-4 w-32 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse" />
                        <div className="h-4 w-32 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse" />
                    </div>
                </div>
                <div className="flex flex-col my-1 px-4 w-full">
                    <div className="flex flex-row gap-6 pt-1">
                        {["w-24", "w-24"].map((w, i) => (
                            <div key={i} className={`${w} h-4 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse mb-3`} />
                        ))}
                    </div>
                </div>
            </div>
            {/* Content skeleton */}
            <div className="bg-very-light-gray dark:bg-off-black flex flex-col flex-grow w-full min-w-[400px] mt-1 py-6 px-8">
                <div className="flex flex-col gap-5 w-full max-w-2xl">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="flex flex-col gap-2">
                            <div className="h-4 w-32 bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse" />
                            <div className="h-9 w-full bg-neutral-gray-50 dark:bg-neutral-gray-700 rounded animate-pulse" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div
            key={subPage}
            className="z-20 flex flex-col w-full h-screen bg-white dark:bg-neutral-gray-900 transition-colors duration-300"
        >
            {/* top portion */}
            <div className={connectionDetailsClasses.header}>
                <div className="flex flex-col pb-2 w-full bg-white dark:bg-neutral-gray-900 transition-colors duration-300">
                    <h1 className="text-2xl sm:text-3xl font-semibold text-off-black dark:text-white transition-colors duration-300">{destinationTitle}</h1>
                    <div className="flex flex-row items-center">
                        <label className={connectionDetailsClasses.infoLabel}>{t("Workspace")}: </label><label className={connectionDetailsClasses.infoPill}>{destination?.owner?.name || ''}</label>
                        <label className={connectionDetailsClasses.separator}>&#x2022;</label>
                        <label className={connectionDetailsClasses.infoLabel}>{t("Technology")}: </label><label className={connectionDetailsClasses.infoPill}>{destination?.type?.name || ''}</label>
                    </div>
                </div>
                <div className="flex flex-col my-1 px-4 w-full bg-white dark:bg-neutral-gray-900 transition-colors duration-300">
                    {/* menu tabs */}
                    <div className="w-full flex flex-row font-semibold text-medium-gray dark:text-neutral-gray-400 transition-colors duration-300">
                        <label className={(destSubPage === "configuration") ? "border-2 border-t-white border-l-white border-r-white dark:border-t-neutral-gray-900 dark:border-l-neutral-gray-900 dark:border-r-neutral-gray-900 border-b-status-green text-dark-gray dark:text-white mr-3 py-3 cursor-pointer transition-colors duration-300" : "border-2 border-white dark:border-neutral-gray-900 mr-3 py-3 cursor-pointer transition-colors duration-300"}
                            onClick={() => onSelectSubPage("configuration")}>
                            {t("Configuration")}
                        </label>
                        <label className={(destSubPage === "details") ? "border-2 border-t-white border-l-white border-r-white dark:border-t-neutral-gray-900 dark:border-l-neutral-gray-900 dark:border-r-neutral-gray-900 border-b-status-green text-dark-gray dark:text-white mr-3 py-3 cursor-pointer transition-colors duration-300" : "border-2 border-white dark:border-neutral-gray-900 mr-3 py-3 cursor-pointer transition-colors duration-300"}
                            onClick={() => onSelectSubPage("details")}>
                            {t("Connections")}
                        </label>
                    </div>
                </div>
            </div>

            {/* scrollable content */}
            <div
                key={destSubPage}
                className="bg-very-light-gray dark:bg-off-black flex flex-col overflow-y-auto flex-grow w-full  min-w-[400px] mt-1 scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600"
                ref={scrollableContentRef}
            >
                <div className="py-3 h-full transition-colors duration-300">
                        {(destSubPage === "details") && <DestinationDetailsModalTable
                            connections={connections}
                            requestSort={requestSort}
                            loadingConnections={loadingConnections}
                            onPageClicked={onPageClicked}
                            totalCount={totalCount}
                            pageLoaded={pageLoaded}
                            take={take}
                            offset={offset}
                        />}
                        {(destSubPage === "configuration") && <DestinationDetailsModalConfig
                            currentMemberIsAdmin={currentMemberIsAdmin}
                            destination={destination}
                            organizations={organizations}
                            loadMoreOrgs={loadMoreOrgs}
                            orgInputChange={orgInputChange}
                            onChange={onChangeSelectedDestination}
                            onChangeSelectedDestinationOwner={onChangeSelectedDestinationOwner}
                            onChangeSelectedDestinationConfig={onChangeSelectedDestinationConfig}
                            onTestAction={onTestAction}
                            testActionLoading={testActionLoading}
                            testActionResponse={testActionResponse}
                            testActionStatus={testActionStatus}
                            onSave={onSave}
                            onToggleModal={onToggleModal}
                            errors={errors}
                            rsjfErrors={rsjfErrors}
                        />}
                    </div>
            </div>
        </div>
    );
};

export default DestinationDetailsModal;